import { config } from '../config';
import { authService } from './authService';

export type ConnectionStatus = 'connected' | 'reconnecting' | 'disconnected' | 'failed';

export interface NetworkUpdateEvent {
  type: 'connected' | 'heartbeat' | 'element-update' | 'element-delete' | 'element-create' | 'network-reload' | 'connection-status';
  timestamp: string;
  elementId?: string;
  zoneId?: string;
  data?: any;
}

// NOTE: LoadFlowResult types removed - now in loadFlowStore.ts
// Power flow data is fetched via REST polling, not SSE

/**
 * Network Updates Service - Zone-Aware SSE Connection
 *
 * This service manages Server-Sent Events for real-time network updates.
 * Each connection is tied to a specific zone. When switching zones:
 * 1. disconnect() from current zone
 * 2. connect(newZoneId) to new zone
 *
 * IMPORTANT: Does NOT auto-connect - must be explicitly connected with a zone ID.
 */
class NetworkUpdatesService {
  private eventSource: EventSource | null = null;
  private currentZoneId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second
  private reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private listeners: Map<string, Set<(event: NetworkUpdateEvent) => void>> = new Map();

  constructor() {
    // IMPORTANT: Do NOT auto-connect here!
    // Connection must be explicitly initiated with a zone ID after the app loads
  }

  /**
   * Connect to SSE for a specific zone.
   * If already connected to the same zone, does nothing.
   * If connected to a different zone, disconnects first then reconnects.
   *
   * @param zoneId The zone to connect to
   */
  connect(zoneId: string) {
    // Guard against server-side execution
    if (typeof window === 'undefined') {
      return;
    }

    // Guard against missing zone
    if (!zoneId) {
      console.warn('[NetworkUpdates] Cannot connect without a zone ID');
      return;
    }

    // If already connected to the same zone, do nothing
    if (this.currentZoneId === zoneId && this.eventSource && this.eventSource.readyState !== EventSource.CLOSED) {
      return;
    }

    // If connected to a different zone, disconnect first
    if (this.eventSource) {
      this.disconnect();
    }

    this.currentZoneId = zoneId;
    this.reconnectAttempts = 0;
    this.reconnectDelay = 1000;

    this.establishConnection();
  }

  private establishConnection() {
    if (!this.currentZoneId) {
      console.warn('[NetworkUpdates] Cannot establish connection without a zone ID');
      return;
    }

    const token = authService.getToken();
    // Zone-aware endpoint: /api/zones/{zoneId}/network/updates/stream
    const baseUrl = `${config.apiBaseUrl}/api/zones/${this.currentZoneId}/network/updates/stream`;
    const url = token ? `${baseUrl}?access_token=${encodeURIComponent(token)}` : baseUrl;

    this.eventSource = new EventSource(url);

    this.eventSource.addEventListener('connected', (event) => {
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      const data = JSON.parse(event.data);
      this.notifyListeners({
        type: 'connected',
        timestamp: new Date().toISOString(),
        zoneId: this.currentZoneId ?? undefined,
        data
      });
    });

    this.eventSource.addEventListener('heartbeat', (event) => {
      const data = JSON.parse(event.data);
      this.notifyListeners({
        type: 'heartbeat',
        timestamp: data.timestamp,
        zoneId: this.currentZoneId ?? undefined
      });
    });

    this.eventSource.addEventListener('element-update', (event) => {
      const data = JSON.parse(event.data);
      this.notifyListeners({
        type: 'element-update',
        timestamp: new Date().toISOString(),
        elementId: data.elementId,
        zoneId: data.zoneId,
        data: data.element
      });
    });

    this.eventSource.addEventListener('element-delete', (event) => {
      const data = JSON.parse(event.data);
      this.notifyListeners({
        type: 'element-delete',
        timestamp: new Date().toISOString(),
        elementId: data.elementId,
        zoneId: data.zoneId
      });
    });

    this.eventSource.addEventListener('element-create', (event) => {
      const data = JSON.parse(event.data);
      this.notifyListeners({
        type: 'element-create',
        timestamp: new Date().toISOString(),
        elementId: data.elementId,
        zoneId: data.zoneId,
        data: data.element
      });
    });

    this.eventSource.addEventListener('network-reload', (event) => {
      const data = JSON.parse(event.data);
      this.notifyListeners({
        type: 'network-reload',
        timestamp: new Date().toISOString(),
        zoneId: data.zoneId,
        data
      });
    });

    // NOTE: load-flow-result SSE event removed - using REST polling instead
    // Frontend now fetches power flow data via GET /powercalculation every 10 seconds

    this.eventSource.onerror = (error) => {
      console.error('[NetworkUpdates] Connection error:', error);
      this.handleConnectionError();
    };

    this.eventSource.onopen = () => {
    };
  }

  private handleConnectionError() {
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }

    // Only reconnect if we have a zone and haven't exceeded attempts
    if (this.currentZoneId && this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;

      this.notifyListeners({
        type: 'connection-status',
        timestamp: new Date().toISOString(),
        data: { status: 'reconnecting' as ConnectionStatus, attempt: this.reconnectAttempts, maxAttempts: this.maxReconnectAttempts }
      });

      this.reconnectTimeoutId = setTimeout(() => {
        this.reconnectTimeoutId = null;
        // Only reconnect if zone is still set (not disconnected intentionally)
        if (this.currentZoneId) {
          this.establishConnection();
        }
      }, this.reconnectDelay);

      // Exponential backoff
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000); // Max 30 seconds
    } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[NetworkUpdates] Max reconnection attempts reached.');
      this.notifyListeners({
        type: 'connection-status',
        timestamp: new Date().toISOString(),
        data: { status: 'failed' as ConnectionStatus, attempt: this.reconnectAttempts, maxAttempts: this.maxReconnectAttempts }
      });
    }
  }

  /**
   * Disconnect from SSE and clear zone.
   * Call this when switching zones or entering zoneless state.
   */
  disconnect() {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
    if (this.eventSource) {
      this.eventSource.close();
      this.eventSource = null;
    }
    this.currentZoneId = null;
    this.reconnectAttempts = 0;
  }

  /**
   * Get the currently connected zone ID, or null if not connected.
   */
  getCurrentZoneId(): string | null {
    return this.currentZoneId;
  }

  subscribe(eventType: NetworkUpdateEvent['type'], callback: (event: NetworkUpdateEvent) => void): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }

    this.listeners.get(eventType)!.add(callback);

    // Return unsubscribe function
    return () => {
      const callbacks = this.listeners.get(eventType);
      if (callbacks) {
        callbacks.delete(callback);
      }
    };
  }

  private notifyListeners(event: NetworkUpdateEvent) {
    const callbacks = this.listeners.get(event.type);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(event);
        } catch (error) {
          console.error('[NetworkUpdates] Error in listener callback:', error);
        }
      });
    }
  }

  isConnected(): boolean {
    return this.eventSource !== null && this.eventSource.readyState === EventSource.OPEN;
  }

  getConnectionState(): string {
    if (!this.eventSource) return 'DISCONNECTED';
    switch (this.eventSource.readyState) {
      case EventSource.CONNECTING:
        return 'CONNECTING';
      case EventSource.OPEN:
        return 'CONNECTED';
      case EventSource.CLOSED:
        return 'CLOSED';
      default:
        return 'UNKNOWN';
    }
  }
}

// Create singleton instance (does NOT auto-connect)
export const networkUpdatesService = new NetworkUpdatesService();
